'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface ResourceCardProps {
  resource: {
    id: string;
    title: string;
    description: string;
    category: string;
    type: string;
    image: string;
    alt: string;
    downloadSize: string;
    lastUpdated: string;
    downloads: number;
    rating: number;
    tags: string[];
  };
  onDownload: (id: string) => void;
  onBookmark: (id: string) => void;
  isBookmarked: boolean;
}

export default function ResourceCard({ resource, onDownload, onBookmark, isBookmarked }: ResourceCardProps) {
  const [isHydrated, setIsHydrated] = useState(false);

  useState(() => {
    setIsHydrated(true);
  });

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-lg border border-border overflow-hidden hover:border-accent transition-all duration-300">
        <div className="relative h-48 bg-muted"></div>
        <div className="p-6">
          <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
          <div className="h-3 bg-muted rounded w-full mb-4"></div>
          <div className="flex items-center justify-between">
            <div className="h-8 bg-muted rounded w-24"></div>
            <div className="h-8 bg-muted rounded w-8"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden hover:border-accent transition-all duration-300 hover:shadow-aerospace group">
      <div className="relative h-48 overflow-hidden">
        <AppImage
          src={resource.image}
          alt={resource.alt}
          className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute top-4 left-4">
          <span className="px-3 py-1 bg-accent/90 text-primary font-cta text-xs rounded-md">
            {resource.type}
          </span>
        </div>
        <div className="absolute top-4 right-4">
          <button
            onClick={() => onBookmark(resource.id)}
            className="p-2 bg-card/90 rounded-lg hover:bg-accent/90 transition-colors duration-300"
            aria-label={isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
          >
            <Icon
              name="BookmarkIcon"
              variant={isBookmarked ? 'solid' : 'outline'}
              size={20}
              className={isBookmarked ? 'text-accent' : 'text-foreground'}
            />
          </button>
        </div>
      </div>

      <div className="p-6">
        <div className="flex items-center space-x-2 mb-3">
          <span className="px-2 py-1 bg-muted text-technical-gray font-body text-xs rounded">
            {resource.category}
          </span>
          <div className="flex items-center space-x-1">
            <Icon name="StarIcon" variant="solid" size={14} className="text-warning" />
            <span className="font-body text-xs text-technical-gray">{resource.rating}</span>
          </div>
        </div>

        <h3 className="font-headline text-lg font-semibold text-foreground mb-2 line-clamp-2 group-hover:text-accent transition-colors duration-300">
          {resource.title}
        </h3>

        <p className="font-body text-sm text-technical-gray mb-4 line-clamp-2">
          {resource.description}
        </p>

        <div className="flex flex-wrap gap-2 mb-4">
          {resource.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="px-2 py-1 bg-muted/50 text-technical-gray font-body text-xs rounded border border-border"
            >
              {tag}
            </span>
          ))}
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="flex items-center space-x-4 text-xs text-technical-gray font-body">
            <div className="flex items-center space-x-1">
              <Icon name="ArrowDownTrayIcon" size={14} />
              <span>{resource.downloads}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="DocumentIcon" size={14} />
              <span>{resource.downloadSize}</span>
            </div>
          </div>

          <button
            onClick={() => onDownload(resource.id)}
            className="px-4 py-2 bg-accent text-primary font-cta text-xs rounded-lg hover:bg-accent/80 transition-all duration-300 hover:shadow-glow"
          >
            DOWNLOAD
          </button>
        </div>

        <div className="mt-3 text-xs text-technical-gray font-body">
          Updated: {resource.lastUpdated}
        </div>
      </div>
    </div>
  );
}